import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { dadosCombinacao, montarExerciciosDoTreino } from '../../lib/bibliotecaTreinos';

const PROTOCOLOS = {
  '30x30': {
    nome: '30x30',
    emoji: '⏱',
    cor: '#B5622E',
    corBg: 'rgba(181,98,46,0.09)',
    corBorda: 'rgba(181,98,46,0.28)',
    resumo: 'Alta intensidade por tempo — 4 séries de 30 segundos de execução com 30 segundos de recuperação.',
    regras: [
      'Cronometrar sempre — é a base do protocolo.',
      'Cargas de baixa intensidade (mais repetições), velocidade rápida/moderada mantendo a postura.',
      'Sexta-feira: dedicar tempo maior para alongar posteriores, lombar e dorsal (~5 min após o cardio).',
      'ABS em todos os treinos, de A a E.',
    ],
  },
  moderado: {
    nome: 'Moderado',
    emoji: '🔁',
    cor: '#3E6E93',
    corBg: 'rgba(62,110,147,0.09)',
    corBorda: 'rgba(62,110,147,0.28)',
    resumo: 'Séries e repetições tradicionais, com progressão de carga dentro do próprio treino.',
    regras: [
      'Carga Leve/Média: iniciar fácil e terminar ainda em carga média. Se terminar pesada, reduzir no próximo treino.',
      'Alongamento geral após o cardio (~5 min), se possível.',
      'ABS nos treinos A, C e E.',
      'Panturrilha nos treinos B e D.',
    ],
  },
  definicao4s: {
    nome: 'Definição · 4 semanas',
    emoji: '⚡',
    cor: '#6B4FA8',
    corBg: 'rgba(107,79,168,0.09)',
    corBorda: 'rgba(107,79,168,0.28)',
    resumo: 'Divisão A–E para condicionamento e definição, com cargas médias, progressões e técnicas de cadência.',
    regras: [
      'Manter as cargas médias durante as quatro semanas e perceber a intensidade pelo número de repetições.',
      'Respeitar a cadência, a posição de mãos/pés e os descansos descritos em cada exercício.',
      'Realizar o bloco de abdômen e panturrilhas em dias alternados.',
      'Reavaliar a divisão ao final das quatro semanas.',
    ],
  },
};

const DIAS_ORDEM = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira'];
const DIA_ABREV = { 'Segunda-feira': 'SEG', 'Terça-feira': 'TER', 'Quarta-feira': 'QUA', 'Quinta-feira': 'QUI', 'Sexta-feira': 'SEX' };

function ExercicioLinha({ ex, cor }) {
  return (
    <div style={s.exLinha}>
      <div style={{ ...s.exBolinha, background: cor }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.exNome}>{ex.nome}</div>
        {ex.execucao && ex.execucao !== '—' && <div style={s.exExecucao}>{ex.execucao}</div>}
        <div style={s.exChipsRow}>
          <span style={{ ...s.exChip, color: cor, background: 'transparent', border: `1px solid ${cor}55` }}>{ex.series}</span>
          {ex.carga && ex.carga !== '—' && <span style={s.exChip}>🏋️ {ex.carga}</span>}
          {ex.posicionamento && ex.posicionamento !== '—' && <span style={s.exChip}>📍 {ex.posicionamento}</span>}
          {ex.descanso && ex.descanso !== '—' && <span style={s.exChip}>⏳ {ex.descanso}</span>}
        </div>
      </div>
    </div>
  );
}

function TreinoCard({ treino, protocolo, onAplicar }) {
  const [aberto, setAberto] = useState(false);
  const cfg = PROTOCOLOS[protocolo];
  const extra = treino.categoria === 'bloco_extra';

  return (
    <div style={{ ...s.card, borderColor: aberto ? cfg.corBorda : 'var(--divider)', borderStyle: extra ? 'dashed' : 'solid' }}>
      <div style={s.cardHeader} onClick={() => setAberto(a => !a)}>
        <div style={{ ...s.letraBadge, background: extra ? 'var(--surface-2)' : cfg.corBg, color: extra ? cfg.cor : cfg.cor }}>
          {extra ? '＋' : treino.letra}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.cardTitulo}>{treino.titulo}</div>
          <div style={s.cardMetaRow}>
            {treino.dia_semana && <span style={s.metaTag}>{treino.dia_semana}</span>}
            <span style={s.metaTag}>{treino.exercicios.length} exercício{treino.exercicios.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth="2" strokeLinecap="round" style={{ transform: aberto ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {treino.foco && <div style={s.focoTexto}>{treino.foco}</div>}

      {aberto && (
        <div style={s.exList}>
          {treino.instrucoes && <div style={{ ...s.instrucoesBox, borderColor: cfg.corBorda, background: cfg.corBg }}>{treino.instrucoes}</div>}
          {treino.exercicios.map((ex, i) => {
            const combinacao = dadosCombinacao(ex);
            const anterior = i > 0 ? dadosCombinacao(treino.exercicios[i - 1]) : null;
            const proximo = i < treino.exercicios.length - 1 ? dadosCombinacao(treino.exercicios[i + 1]) : null;
            const abre = combinacao && (!anterior || anterior.id !== combinacao.id);
            const fecha = combinacao && (!proximo || proximo.id !== combinacao.id);
            return (
              <React.Fragment key={i}>
                {abre && (
                  <div style={{ marginTop:10, padding:'9px 11px 7px', border:`1.5px solid ${cfg.corBorda}`, borderBottom:0, borderRadius:'14px 14px 0 0', background:cfg.corBg }}>
                    <div style={{ color:cfg.cor, fontSize:10, fontWeight:800, letterSpacing:'0.09em', textTransform:'uppercase' }}>{combinacao.tipo}</div>
                    <div style={{ marginTop:2, color:'var(--text-2)', fontSize:10.5, lineHeight:1.4 }}>Executar em sequência · descanso somente após o último exercício</div>
                  </div>
                )}
                <div style={combinacao ? { padding:'0 8px', borderLeft:`1.5px solid ${cfg.corBorda}`, borderRight:`1.5px solid ${cfg.corBorda}`, background:cfg.corBg } : undefined}>
                  <ExercicioLinha ex={ex} cor={cfg.cor} />
                </div>
                {fecha && <div style={{ height:8, marginBottom:8, border:`1.5px solid ${cfg.corBorda}`, borderTop:0, borderRadius:'0 0 14px 14px', background:cfg.corBg }} />}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <button
        style={{ ...s.aplicarBtn, background: cfg.cor }}
        onClick={(e) => { e.stopPropagation(); onAplicar(treino); }}
      >
        Aplicar em uma aluna →
      </button>
    </div>
  );
}

function ModalSemanaCompleta({ protocolo, cfg, principais, treinosBiblioteca, onClose }) {
  const { alunas } = useAuth();
  const { salvarTreino } = useData();
  const navigate = useNavigate();
  const [alunaId, setAlunaId] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [concluido, setConcluido] = useState(false);
  const [erroAplicacao, setErroAplicacao] = useState('');

  const alunasAtivas = (alunas || []).filter(a => a.status === 'ativa');
  const aluna = alunasAtivas.find(a => a.id === alunaId);

  async function confirmar() {
    if (!alunaId) return;
    setAplicando(true);
    setErroAplicacao('');
    try {
      for (const treino of principais) {
        await salvarTreino({
          alunaId,
          titulo: treino.titulo,
          descricao: treino.foco,
          obs: treino.instrucoes || null,
          semana: 1,
          ordem: treino.ordem,
          exercicios: montarExerciciosDoTreino(treino, treinosBiblioteca),
        });
      }
      setConcluido(true);
    } catch (error) {
      console.error('Erro ao aplicar protocolo completo:', error);
      setErroAplicacao('Não foi possível salvar todas as fichas. Tente novamente; os treinos já gravados continuam disponíveis em “Treinos”.');
    } finally {
      setAplicando(false);
    }
  }

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modalCard} onClick={e => e.stopPropagation()}>
        {concluido ? (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
            <div style={s.modalTitulo}>Protocolo aplicado!</div>
            <div style={s.modalTexto}>
              {principais.length} treinos ({principais.map(t => t.letra).join(', ')}) foram criados para <strong>{aluna?.name}</strong>. Você pode revisar e ajustar cada um em "Treinos".
            </div>
            <div style={s.modalActions}>
              <button style={{ ...s.modalBtnPrimary, background: cfg.cor }} onClick={() => { onClose(); navigate('/admin/treinos'); }}>Ver treinos</button>
              <button style={s.modalBtnGhost} onClick={onClose}>Fechar</button>
            </div>
          </>
        ) : (
          <>
            <div style={s.modalTitulo}>Aplicar protocolo {cfg.nome} completo</div>
            <div style={s.modalTexto}>Cria de uma vez os {principais.length} treinos ({principais.map(t => t.letra).join(', ')}) para a aluna escolhida, já com os blocos de ABS/panturrilha somados. Dá pra ajustar cada ficha depois.</div>

            <label style={s.modalLabel}>Aluna</label>
            <select style={s.modalInput} value={alunaId} onChange={e => setAlunaId(e.target.value)}>
              <option value="">Selecionar aluna</option>
              {alunasAtivas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>

            <div style={{ ...s.modalInput, height:'auto', lineHeight:1.5, color:'var(--text-2)' }}>
              <strong style={{ color:'var(--text)' }}>Duração do ciclo:</strong> {protocolo === 'definicao4s' ? '4 semanas com estas mesmas fichas.' : 'as fichas permanecem ativas até a próxima reavaliação.'}<br />
              Não é necessário cadastrar o treino novamente toda semana.
            </div>

            <div style={s.modalPreviewRow}>
              {principais.map(t => (
                <span key={t.id} style={{ ...s.modalPreviewChip, borderColor: cfg.corBorda, color: cfg.cor }}>{t.letra} · {t.dia_semana?.replace('-feira', '')}</span>
              ))}
            </div>

            {erroAplicacao && (
              <div style={{ padding: '10px 12px', marginBottom: 12, borderRadius: 14, background: 'rgba(229,57,53,0.07)', border: '1px solid rgba(229,57,53,0.2)', color: '#C62828', fontSize: 12, lineHeight: 1.5 }}>
                {erroAplicacao}
              </div>
            )}

            <div style={s.modalActions}>
              <button
                style={{ ...s.modalBtnPrimary, background: cfg.cor, opacity: alunaId && !aplicando ? 1 : 0.5 }}
                onClick={confirmar}
                disabled={!alunaId || aplicando}
              >
                {aplicando ? 'Aplicando...' : `Aplicar ${principais.length} treinos →`}
              </button>
              <button style={s.modalBtnGhost} onClick={onClose} disabled={aplicando}>Cancelar</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function BibliotecaTreinos() {
  const { treinosBiblioteca } = useData();
  const navigate = useNavigate();
  const [protocoloAtivo, setProtocoloAtivo] = useState('30x30');
  const [modalAberto, setModalAberto] = useState(false);

  const doProtocolo = treinosBiblioteca.filter(t => t.protocolo === protocoloAtivo);
  const principais = doProtocolo.filter(t => t.categoria === 'principal').sort((a, b) => a.ordem - b.ordem);
  const extras = doProtocolo.filter(t => t.categoria === 'bloco_extra');
  const cfg = PROTOCOLOS[protocoloAtivo];

  function aplicarNaAluna(treino) {
    navigate(`/admin/treino/novo?template=${treino.id}`);
  }

  return (
    <AdminLayout>
      <style>{`
        .prot-tab { transition: all 0.18s ease; }
        .prot-tab:hover { transform: translateY(-1px); }
        .bib-card { transition: box-shadow 0.2s ease, transform 0.15s ease; }
        .bib-card:hover { box-shadow: var(--sh-card); }
      `}</style>

      <h1 style={s.h1}>Biblioteca de <em style={s.h1em}>Treinos</em></h1>
      <p style={s.sub}>Fichas prontas do método Team Carol Macedo. Escolha um treino e aplique direto para a aluna — ajuste o que precisar antes de salvar.</p>

      <div style={s.tabRow}>
        {Object.entries(PROTOCOLOS).map(([key, p]) => (
          <div
            key={key}
            className="prot-tab"
            onClick={() => setProtocoloAtivo(key)}
            style={{
              ...s.tab,
              background: protocoloAtivo === key ? p.cor : 'var(--surface)',
              color: protocoloAtivo === key ? '#fff' : 'var(--text)',
              borderColor: protocoloAtivo === key ? p.cor : 'var(--divider)',
            }}
          >
            <span style={{ fontSize: 18 }}>{p.emoji}</span>
            <span>Protocolo {p.nome}</span>
            <span style={{ ...s.tabCount, background: protocoloAtivo === key ? 'rgba(255,255,255,0.25)' : 'var(--surface-2)', color: protocoloAtivo === key ? '#fff' : 'var(--text-2)' }}>
              {treinosBiblioteca.filter(t => t.protocolo === key && t.categoria === 'principal').length} treinos
            </span>
          </div>
        ))}
      </div>

      <div style={{ ...s.overviewCard, background: cfg.corBg, borderColor: cfg.corBorda }}>
        <div style={{ ...s.overviewTitulo, color: cfg.cor }}>{cfg.emoji} {cfg.resumo}</div>
        <ul style={s.regrasList}>
          {cfg.regras.map((r, i) => <li key={i} style={s.regraItem}>{r}</li>)}
        </ul>
        <button style={{ ...s.semanaCompletaBtn, background: cfg.cor }} onClick={() => setModalAberto(true)}>
          📆 Aplicar protocolo {cfg.nome} completo numa aluna →
        </button>
      </div>

      {modalAberto && (
        <ModalSemanaCompleta
          protocolo={protocoloAtivo}
          cfg={cfg}
          principais={principais}
          treinosBiblioteca={treinosBiblioteca}
          onClose={() => setModalAberto(false)}
        />
      )}

      <div style={s.diasStrip}>
        {DIAS_ORDEM.map(dia => {
          const treino = principais.find(t => t.dia_semana === dia);
          return (
            <div key={dia} style={s.diaChip}>
              <div style={{ ...s.diaAbrev, color: cfg.cor }}>{DIA_ABREV[dia]}</div>
              <div style={s.diaLetra}>{treino?.letra || '—'}</div>
              <div style={s.diaFoco}>{treino?.foco?.split(' + ')[0] || ''}</div>
            </div>
          );
        })}
      </div>

      <div style={s.sectionLabel}>Treinos principais</div>
      <div style={s.grid} className="bib-grid">
        {principais.map(t => (
          <div className="bib-card" key={t.id}>
            <TreinoCard treino={t} protocolo={protocoloAtivo} onAplicar={aplicarNaAluna} />
          </div>
        ))}
      </div>

      {extras.length > 0 && (
        <>
          <div style={s.sectionLabel}>Blocos extras (somar ao treino principal)</div>
          <div style={s.grid}>
            {extras.map(t => (
              <div className="bib-card" key={t.id}>
                <TreinoCard treino={t} protocolo={protocoloAtivo} onAplicar={aplicarNaAluna} />
              </div>
            ))}
          </div>
        </>
      )}

      {doProtocolo.length === 0 && (
        <div style={s.empty}>Nenhum treino cadastrado nessa biblioteca ainda.</div>
      )}
    </AdminLayout>
  );
}

const s = {
  h1: { fontFamily: "'Inter', sans-serif", fontSize: 34, fontWeight: 700, color: 'var(--text)', marginBottom: 4 },
  h1em: { fontStyle: 'italic', color: 'var(--accent)' },
  sub: { fontSize: 13, color: 'var(--text-2)', marginBottom: 24, maxWidth: 620, lineHeight: 1.6 },

  tabRow: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  tab: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderRadius: 'var(--r-btn)', border: '1.5px solid', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif" },
  tabCount: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, marginLeft: 2 },

  overviewCard: { border: '1.5px solid', borderRadius: 'var(--r-card)', padding: '18px 22px', marginBottom: 20 },
  overviewTitulo: { fontSize: 14, fontWeight: 700, marginBottom: 10 },
  regrasList: { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 },
  regraItem: { fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6 },
  semanaCompletaBtn: { marginTop: 16, width: '100%', padding: '13px', border: 'none', borderRadius: 'var(--r-btn)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif" },

  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(37,28,25,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20 },
  modalCard: { background: 'var(--bg)', padding: 28, borderRadius: 'var(--r-card)', maxWidth: 420, width: '100%' },
  modalTitulo: { fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 8 },
  modalTexto: { fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 18 },
  modalLabel: { display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 5, marginTop: 12 },
  modalInput: { width: '100%', padding: '10px 12px', border: '1.5px solid var(--divider)', borderRadius: 'var(--r-input)', fontSize: 13, color: 'var(--text)', background: 'var(--surface)', outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter',sans-serif" },
  modalPreviewRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 },
  modalPreviewChip: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 100, border: '1.5px solid' },
  modalActions: { display: 'flex', gap: 10, marginTop: 22 },
  modalBtnPrimary: { flex: 1, padding: 12, border: 'none', color: '#fff', borderRadius: 'var(--r-btn)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif" },
  modalBtnGhost: { flex: 1, padding: 12, background: 'transparent', border: '1px solid var(--divider)', color: 'var(--text-2)', borderRadius: 'var(--r-btn)', fontSize: 13, cursor: 'pointer', fontFamily: "'Inter',sans-serif" },

  diasStrip: { display: 'flex', gap: 8, marginBottom: 28, overflowX: 'auto', paddingBottom: 4 },
  diaChip: { flex: '1 1 0', minWidth: 84, background: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 16, padding: '10px 8px', textAlign: 'center', boxShadow: 'var(--sh-card)' },
  diaAbrev: { fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4 },
  diaLetra: { fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', lineHeight: 1 },
  diaFoco: { fontSize: 9.5, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.3, minHeight: 24 },

  sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: 12, marginTop: 8 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 14, marginBottom: 28 },

  card: { background: 'var(--surface)', border: '1.5px solid var(--divider)', borderRadius: 'var(--r-card)', padding: '18px 20px', boxShadow: 'var(--sh-card)', height: '100%', display: 'flex', flexDirection: 'column' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' },
  letraBadge: { width: 40, height: 40, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 700, flexShrink: 0 },
  cardTitulo: { fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 3 },
  cardMetaRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  metaTag: { fontSize: 10.5, color: 'var(--text-2)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 100 },
  focoTexto: { fontSize: 11.5, color: 'var(--text-2)', marginTop: 10, paddingLeft: 52, lineHeight: 1.5 },

  exList: { marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--divider)', display: 'flex', flexDirection: 'column', gap: 10 },
  instrucoesBox: { fontSize: 11.5, color: 'var(--text-3)', border: '1px solid', borderRadius: 14, padding: '10px 12px', lineHeight: 1.6, marginBottom: 4 },

  exLinha: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  exBolinha: { width: 7, height: 7, borderRadius: '50%', marginTop: 6, flexShrink: 0 },
  exNome: { fontSize: 12.5, fontWeight: 700, color: 'var(--text)' },
  exExecucao: { fontSize: 11, color: 'var(--text-2)', lineHeight: 1.5, marginTop: 2 },
  exChipsRow: { display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 },
  exChip: { fontSize: 10, fontWeight: 600, color: 'var(--text-2)', background: 'var(--surface-2)', padding: '3px 8px', borderRadius: 100, whiteSpace: 'nowrap' },

  aplicarBtn: { marginTop: 16, width: '100%', padding: '11px', border: 'none', borderRadius: 'var(--r-btn)', color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', cursor: 'pointer', fontFamily: "'Inter',sans-serif" },

  empty: { padding: '48px 32px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 'var(--r-card)', color: 'var(--text-2)', fontSize: 13 },
};
